/**
 * F43 (Track B) — Report detail.
 *
 * Route: /reports/:id
 * Permission: reports:read
 *
 * Admin-only reporter + subject inline preview + evidence ImageLightbox +
 * AuditLogTimeline + actions footer (Action → deep-link to the owning surface /
 * Dismiss with reason / Reassign / releasable Claim). For
 * `subject_type='chat_message'`: renders the ChatModerationThread from the
 * chat-context endpoint with the "Moderation view — every read is logged"
 * banner + a soft-delete action (gated `chat:moderate`).
 *
 * Data source: the F43 `reportsApi` (aliased to its fixture in tests).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  ChatModerationThread,
  Dialog,
  FormField,
  ImageLightbox,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bravemove/ui-web';
import {
  CHAT_CONTEXT_DEFAULT_WINDOW,
  type ChatContextWindow,
  type ReportDetail,
} from '@bravemove/types';
import { reportsApi, type AssignableAdmin } from '../../api/reports';
import { useAuth } from '../../auth/useAuth';
import { translateApiError } from '../../auth/errors.js';
import { PageHeader } from '../../components/PageHeader';

const STATUS_TONES: Record<string, 'success' | 'warning' | 'neutral' | 'danger' | 'info'> = {
  open: 'warning',
  under_review: 'info',
  actioned: 'success',
  dismissed: 'neutral',
};

export function AdminReportDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useI18n();
  const navigate = useNavigate();
  const auth = useAuth();
  const toast = useToast();

  const canResolve = auth.adminUser?.permissions?.includes('reports:resolve') ?? false;
  const canViewChat = auth.adminUser?.permissions?.includes('chat:view_reported') ?? false;
  const canModerateChat = auth.adminUser?.permissions?.includes('chat:moderate') ?? false;

  const [report, setReport] = useState<ReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [chatWindow, setChatWindow] = useState<ChatContextWindow | null>(null);
  const [chatError, setChatError] = useState<string | null>(null);

  // PU-06 — the evidence is in the PRIVATE `report-evidence` bucket; the stored
  // `evidenceUrl` is an S3 key. The lightbox opens on a fetched 5-min presigned
  // GET, never the raw key (which 403s in production).
  const [evidenceSrc, setEvidenceSrc] = useState<string | null>(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);

  // PU-10 — the assignable admin-staff list for the Reassign dialog. Empty when
  // the actor lacks `admin_users:read` (the client degrades a 403 to []) — the
  // dialog then falls back to the self-assign ('Me') option.
  const [assignableAdmins, setAssignableAdmins] = useState<readonly AssignableAdmin[]>([]);

  // Action dialogs
  const [actionOpen, setActionOpen] = useState(false);
  const [actionText, setActionText] = useState('');
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignId, setReassignId] = useState('');
  const [softDeleteId, setSoftDeleteId] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await reportsApi.getDetail(id);
      setReport(r);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchReport();
  }, [fetchReport]);

  // Load the chat-context window once the report is known to be a chat report.
  useEffect(() => {
    if (!report || report.subjectType !== 'chat_message' || !canViewChat) return;
    let cancelled = false;
    void (async () => {
      try {
        const win = await reportsApi.getChatContext(id, {
          before: CHAT_CONTEXT_DEFAULT_WINDOW,
          after: CHAT_CONTEXT_DEFAULT_WINDOW,
        });
        if (!cancelled) setChatWindow(win);
      } catch (err) {
        if (!cancelled) setChatError(translateApiError(err, t));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [report, id, canViewChat, t]);

  // PU-10 — fetch the admin-staff list once for resolvers.
  useEffect(() => {
    if (!canResolve) return;
    let cancelled = false;
    void reportsApi.listAssignableAdmins().then((admins) => {
      if (!cancelled) setAssignableAdmins(admins);
    });
    return () => {
      cancelled = true;
    };
  }, [canResolve]);

  // PU-06 — fetch a fresh presigned GET, then open the lightbox on it.
  const openEvidence = useCallback(async () => {
    setEvidenceLoading(true);
    try {
      const { url } = await reportsApi.getEvidenceDownloadUrl(id);
      setEvidenceSrc(url);
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setEvidenceLoading(false);
    }
  }, [id, toast, t]);

  const refetchChat = useCallback(async () => {
    try {
      const win = await reportsApi.getChatContext(id, {
        before: CHAT_CONTEXT_DEFAULT_WINDOW,
        after: CHAT_CONTEXT_DEFAULT_WINDOW,
      });
      setChatWindow(win);
    } catch {
      /* keep prior window */
    }
  }, [id]);

  const runClaim = async (claim: boolean) => {
    setBusy(true);
    try {
      const next = claim ? await reportsApi.claim(id) : await reportsApi.unclaim(id);
      setReport(next);
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setBusy(false);
    }
  };

  const runAction = async () => {
    const trimmed = actionText.trim();
    if (trimmed.length === 0) return;
    setBusy(true);
    try {
      const next = await reportsApi.action(id, { actionTaken: trimmed });
      setReport(next);
      toast.push({ tone: 'success', title: t('moderation:action.action') });
      setActionOpen(false);
      setActionText('');
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setBusy(false);
    }
  };

  const runDismiss = async () => {
    const trimmed = dismissReason.trim();
    if (trimmed.length === 0) return;
    setBusy(true);
    try {
      const next = await reportsApi.dismiss(id, { resolutionReason: trimmed });
      setReport(next);
      toast.push({ tone: 'success', title: t('moderation:action.dismiss') });
      setDismissOpen(false);
      setDismissReason('');
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setBusy(false);
    }
  };

  const runReassign = async () => {
    if (reassignId.trim().length === 0) return;
    setBusy(true);
    try {
      const next = await reportsApi.reassign(id, { assignedAdminId: reassignId.trim() });
      setReport(next);
      toast.push({ tone: 'success', title: t('moderation:action.reassign') });
      setReassignOpen(false);
      setReassignId('');
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setBusy(false);
    }
  };

  const runSoftDelete = async () => {
    if (!softDeleteId) return;
    setBusy(true);
    try {
      await reportsApi.softDeleteChatMessage(softDeleteId);
      toast.push({ tone: 'success', title: t('moderation:chat.soft_delete') });
      setSoftDeleteId(null);
      await refetchChat();
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !report) {
    return <Banner tone="danger">{error ?? t('moderation:errors.REPORT_NOT_FOUND')}</Banner>;
  }

  const isChatReport = report.subjectType === 'chat_message';
  const isResolved = report.status === 'actioned' || report.status === 'dismissed';
  // F43 owns the action for user + chat_message; everything else deep-links out.
  const deepLinkable =
    report.deepLinkPath !== null && !isChatReport && report.subjectType !== 'user';

  return (
    <div className="space-y-6" data-testid="admin-report-detail">
      <PageHeader title={t('moderation:detail.title')} />

      {/* Summary card */}
      <Card>
        <div className="space-y-3 p-4" data-testid="f43-report-summary">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral" size="sm">
              {t(`moderation:report.subject_type.${report.subjectType}`)}
            </Badge>
            <Badge tone={STATUS_TONES[report.status] ?? 'neutral'} size="sm">
              {t(`moderation:report.status.${report.status}`)}
            </Badge>
            <Badge tone="warning" size="sm">
              {t(`moderation:report.reason.${report.reason}`)}
            </Badge>
            {report.assignedAdminId ? (
              <Badge tone="info" size="sm" data-testid="f43-report-claimed">
                {t('moderation:action.claimed_by', { name: report.assignedAdminId })}
              </Badge>
            ) : null}
          </div>

          {/* Admin-only reporter identity (D10 — never surfaced to the reported party) */}
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-ink-3">
                {t('moderation:detail.reporter')}
              </p>
              <p className="font-mono text-[12px] text-ink" data-testid="f43-report-reporter">
                {report.reporterUserId}
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-ink-3">
                {t('moderation:detail.subject_preview')}
              </p>
              <p className="font-mono text-[12px] text-ink">{report.subjectId}</p>
            </div>
          </div>

          {report.freeText ? (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-ink-3">
                {t('moderation:detail.free_text')}
              </p>
              <p className="text-[13px] text-ink-2">{report.freeText}</p>
            </div>
          ) : null}

          {/* Evidence */}
          <div>
            <p className="text-[11px] uppercase tracking-wide text-ink-3">
              {t('moderation:detail.evidence')}
            </p>
            {report.evidenceUrl ? (
              <button
                type="button"
                onClick={() => void openEvidence()}
                disabled={evidenceLoading}
                className="mt-1 inline-flex items-center rounded-md border border-outline px-2.5 py-1 text-[12px] text-accent disabled:opacity-50"
                data-testid="f43-report-evidence-open"
              >
                {t('moderation:detail.evidence')}
              </button>
            ) : (
              <p className="text-[12px] text-ink-3">{t('moderation:detail.no_evidence')}</p>
            )}
          </div>

          {/* Resolution (if resolved) */}
          {isResolved ? (
            <div
              className="rounded-md border border-outline bg-surface-2 p-3"
              data-testid="f43-report-resolution"
            >
              <p className="text-[11px] uppercase tracking-wide text-ink-3">
                {t('moderation:detail.resolution')}
              </p>
              {report.actionTaken ? (
                <p className="text-[13px] text-ink">
                  {t('moderation:detail.action_taken')}: {report.actionTaken}
                </p>
              ) : null}
              {report.resolutionReason ? (
                <p className="text-[13px] text-ink-2">
                  {t('moderation:detail.resolution_reason')}: {report.resolutionReason}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>

      {/* Chat-context viewer (subject_type='chat_message') */}
      {isChatReport ? (
        <Card>
          <div className="p-4" data-testid="f43-report-chat-context">
            {!canViewChat ? (
              <Banner tone="info">{t('moderation:chat.read_only')}</Banner>
            ) : chatError ? (
              <Banner tone="danger">{chatError}</Banner>
            ) : chatWindow === null ? (
              <Skeleton className="h-48 w-full rounded-lg" />
            ) : (
              <div className="space-y-3">
                <ChatModerationThread
                  messages={chatWindow.messages}
                  anchorMessageId={chatWindow.anchorMessageId}
                  bannerText={t('moderation:chat.moderation_banner')}
                />
                {canModerateChat ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setSoftDeleteId(chatWindow.anchorMessageId)}
                    data-testid="f43-chat-soft-delete"
                  >
                    {t('moderation:chat.soft_delete')}
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        </Card>
      ) : null}

      {/* Actions footer */}
      {canResolve ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="f43-report-actions">
          {/* FIX journey audit 2.6 (B7): a resolved report is final (409 REPORT_NOT_OPEN). */}
          {isResolved ? null : report.assignedAdminId ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void runClaim(false)}
              disabled={busy}
              data-testid="f43-report-unclaim"
            >
              {t('moderation:action.unclaim')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void runClaim(true)}
              disabled={busy}
              data-testid="f43-report-claim"
            >
              {t('moderation:action.claim')}
            </Button>
          )}

          {deepLinkable ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(report.deepLinkPath!)}
              data-testid="f43-report-deeplink"
            >
              {t('moderation:detail.deep_link')}
            </Button>
          ) : null}

          {!isResolved ? (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setActionOpen(true)}
                data-testid="f43-report-action"
              >
                {t('moderation:action.action')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDismissOpen(true)}
                data-testid="f43-report-dismiss"
              >
                {t('moderation:action.dismiss')}
              </Button>
            </>
          ) : null}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setReassignOpen(true)}
            data-testid="f43-report-reassign"
          >
            {t('moderation:action.reassign')}
          </Button>
        </div>
      ) : null}

      {/* Evidence lightbox — opens on the fetched presigned GET (PU-06). */}
      {evidenceSrc !== null ? (
        <ImageLightbox
          src={evidenceSrc}
          alt={t('moderation:detail.evidence')}
          open
          onClose={() => setEvidenceSrc(null)}
        />
      ) : null}

      {/* Action dialog */}
      <Dialog
        open={actionOpen}
        onClose={() => setActionOpen(false)}
        onConfirm={() => void runAction()}
        title={t('moderation:action.action')}
        confirmLabel={t('moderation:action.action')}
        loading={busy}
      >
        <FormField label={t('moderation:action.action_taken_label')}>
          <Textarea
            value={actionText}
            onChange={(e) => setActionText(e.target.value)}
            placeholder={t('moderation:action.action_taken_placeholder')}
            rows={3}
            data-testid="f43-action-text"
          />
        </FormField>
      </Dialog>

      {/* Dismiss dialog */}
      <Dialog
        open={dismissOpen}
        onClose={() => setDismissOpen(false)}
        onConfirm={() => void runDismiss()}
        title={t('moderation:action.dismiss')}
        confirmLabel={t('moderation:action.dismiss')}
        loading={busy}
      >
        <FormField label={t('moderation:action.dismiss_reason_label')}>
          <Textarea
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            placeholder={t('moderation:action.dismiss_reason_placeholder')}
            rows={3}
            data-testid="f43-dismiss-reason"
          />
        </FormField>
      </Dialog>

      {/* Reassign dialog */}
      <Dialog
        open={reassignOpen}
        onClose={() => setReassignOpen(false)}
        onConfirm={() => void runReassign()}
        title={t('moderation:action.reassign')}
        confirmLabel={t('moderation:action.reassign')}
        loading={busy}
      >
        <FormField label={t('moderation:action.reassign_label')}>
          {/* PU-10 — the full admin-staff list (when readable); falls back to
              the self-assign option for roles without `admin_users:read`. */}
          <Select
            value={reassignId}
            onChange={(e) => setReassignId(e.target.value)}
            options={[
              { value: '', label: '—' },
              ...(assignableAdmins.length > 0
                ? assignableAdmins.map((a) => ({ value: a.id, label: a.label }))
                : [
                    {
                      value: auth.adminUser?.id ?? 'admin-1',
                      label: auth.adminUser?.displayName ?? 'Me',
                    },
                  ]),
            ]}
            data-testid="f43-reassign-select"
          />
        </FormField>
      </Dialog>

      {/* Soft-delete confirm */}
      <Dialog
        open={softDeleteId !== null}
        onClose={() => setSoftDeleteId(null)}
        onConfirm={() => void runSoftDelete()}
        destructive
        title={t('moderation:chat.soft_delete')}
        description={t('moderation:chat.soft_delete_confirm')}
        confirmLabel={t('moderation:chat.soft_delete')}
        loading={busy}
      />
    </div>
  );
}
